 import{ Button,
  Card,
  CardContent,
  CustomAlertDialog,
  PageContainer,
  Spinner,
  ErrorMessage,
} from "@/components";
import { Badge } from "@/components/ui/badge";
import EducationDialog from "@/components/formDialog/EducationDialog";
import {
  useEducationListQuery,
  useDeleteEducationMutation,
} from "@/hooks/api/education.queries";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { EducationType } from "@/services/education.service";
import { 
  GraduationCap, 
  Plus, 
  Pencil, 
  Trash2, 
  Calendar, 
  CheckCircle2, 
  History,
  Building2,
  Trophy
} from "lucide-react";
import { useCallback, useState } from "react";
import { cn } from "@/lib/utils";

export default function Education() {
  const { data: educationList, isLoading, isError, refetch } = useEducationListQuery();
  const deleteMutation = useDeleteEducationMutation();

  const deleteDialog = useAlertDialog();
  const educationDialog = useFormDialog({
    mode: "create" as "create" | "edit",
  });
  const [selectedRecord, setSelectedRecord] = useState<EducationType | null>(null);

  const handleEdit = (record: EducationType) => {
    setSelectedRecord(record);
    educationDialog.open({ mode: "edit" });
  };

  const handleAdd = () => {
    setSelectedRecord(null);
    educationDialog.open({ mode: "create" });
  };

  const handleDeleteConfirm = useCallback(() => {
    if (deleteDialog.state.mantraId) {
      deleteMutation.mutate(deleteDialog.state.mantraId, {
        onSuccess: () => deleteDialog.close(),
      });
    }
  }, [deleteMutation, deleteDialog]);

  const openDeleteDialog = (record: EducationType) => {
    deleteDialog.open({
      message: `Are you sure you want to delete your education record for ${record.college_name}?`,
      mantraId: record.id,
      mantraTitle: record.college_name,
      action: "delete",
    });
  };

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load your education history."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-8 pb-12">
      {/* ─── Header ─── */}
      <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-r from-indigo-700 to-violet-800 p-10 text-white shadow-2xl">
         <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
         <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="space-y-2 text-center md:text-left">
               <div className="flex items-center justify-center md:justify-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-inner">
                    <GraduationCap size={28} className="text-white" />
                  </div>
                  <h1 className="text-3xl md:text-4xl font-black tracking-tight">Academic Journey</h1>
               </div>
               <p className="text-indigo-100/80 font-medium max-w-lg">
                 Document your educational background, from schooling to your current degree. Your history helps us match you with the right opportunities.
               </p>
            </div>
            <Button
              onClick={handleAdd}
              className="bg-white text-indigo-700 hover:bg-indigo-50 font-black px-8 py-6 rounded-2xl shadow-xl transition-all hover:scale-105 active:scale-95 flex items-center gap-3"
            >
              <Plus size={20} />
              Add Milestone
            </Button>
         </div>
      </div>

      {/* ─── Content ─── */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Spinner />
        </div>
      ) : educationList && educationList.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {educationList.map((record) => (
            <Card key={record.id} className="group relative overflow-hidden rounded-[2rem] border-none shadow-xl shadow-slate-200/50 bg-white transition-all hover:shadow-2xl hover:shadow-indigo-100">
               {/* Accent bar */}
               <div className={cn(
                 "absolute top-0 left-0 bottom-0 w-2 transition-all group-hover:w-3",
                 record.status === 'completed' ? "bg-emerald-500" : "bg-indigo-500"
               )} />
               
               <CardContent className="p-8 space-y-6">
                  <div className="flex justify-between items-start">
                     <div className="space-y-1">
                        <div className="flex items-center gap-2">
                           <Badge variant="outline" className={cn(
                             "uppercase text-[10px] font-black px-2 rounded-lg",
                             record.status === 'completed' ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-indigo-50 border-indigo-200 text-indigo-700"
                           )}>
                             {record.degree_type}
                           </Badge>
                           {record.is_alumni && (
                             <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none px-2 rounded-lg text-[10px] flex items-center gap-1 font-black">
                               <Trophy size={10} /> Alumni
                             </Badge>
                           )}
                        </div>
                        <h3 className="text-xl font-black text-slate-900 group-hover:text-indigo-600 transition-colors">{record.degree_name}</h3>
                        <p className="text-slate-500 font-bold flex items-center gap-1.5 text-sm">
                           <Building2 size={14} className="text-slate-400" />
                           {record.college_name}
                        </p>
                     </div>
                     <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-x-4 group-hover:translate-x-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(record)}
                          className="h-9 w-9 rounded-xl text-slate-400 hover:bg-indigo-50 hover:text-indigo-600"
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(record)}
                          className="h-9 w-9 rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                     </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-2">
                     <div className="bg-slate-50 p-4 rounded-2xl space-y-1">
                        <p className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1">
                           <Calendar size={10} /> Timeline
                        </p>
                        <p className="text-sm font-bold text-slate-700">{record.start_year} — {record.end_year}</p>
                     </div>
                     <div className="bg-slate-50 p-4 rounded-2xl space-y-1">
                        <p className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1">
                           <History size={10} /> Status
                        </p>
                        <p className={cn(
                          "text-sm font-bold",
                          record.status === 'completed' ? "text-emerald-600 text-sm" : "text-indigo-600"
                        )}>
                          {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                        </p>
                     </div>
                  </div>

                  <div className="pt-4 border-t border-slate-50 flex items-center justify-between">
                     <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500">
                           <GraduationCap size={16} />
                        </div>
                        <div>
                           <p className="text-[9px] font-black uppercase text-slate-400">Roll/Reg No</p>
                           <p className="text-xs font-bold text-slate-700">{record.roll_number}</p>
                        </div>
                     </div>
                     {record.status === 'completed' && (
                        <div className="flex items-center gap-1.5 text-emerald-500 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-100">
                           <CheckCircle2 size={14} />
                           <span className="text-[10px] font-black uppercase">Verified Milestone</span>
                        </div>
                     )}
                  </div>
               </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-2 border-slate-200 bg-slate-50/50 rounded-[2.5rem] py-20">
          <CardContent className="flex flex-col items-center gap-6 text-center">
            <div className="w-20 h-20 rounded-3xl bg-white shadow-xl flex items-center justify-center text-slate-300">
              <GraduationCap size={40} />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-black text-slate-900">Your academic history is empty</h3>
              <p className="text-slate-500 max-w-sm mx-auto text-sm">
                Add your degrees, certifications, or school details to build a complete profile for potential employers.
              </p>
            </div>
            <Button
              onClick={handleAdd}
              className="bg-indigo-600 hover:bg-indigo-700 rounded-2xl font-bold px-8 shadow-lg shadow-indigo-100"
            >
              Add Your First Qualification
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ─── Dialogs ─── */}
      <EducationDialog
        isOpen={educationDialog.state.isOpen}
        onOpenChange={(open) => !open && educationDialog.close()}
        onCancel={educationDialog.close}
        initialData={selectedRecord}
      />

      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Delete Record"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
}
